import { createContext, useCallback, useEffect, useReducer } from "react";
import PropTypes from "prop-types";
import { authApi } from "src/api/auth";
import { Issuer } from "src/utils/auth";
import axios from "axios";

// eslint-disable-next-line
const STORAGE_KEY = "admin-accessToken";
const userDetails = "userDetails";
const userPermission = "userPermission";

var ActionType;
(function (ActionType) {
  ActionType["INITIALIZE"] = "INITIALIZE";
  ActionType["SIGN_IN"] = "SIGN_IN";
  ActionType["SIGN_UP"] = "SIGN_UP";
  ActionType["SIGN_OUT"] = "SIGN_OUT";
})(ActionType || (ActionType = {}));

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  errorCase: false,
};

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user } = action.payload;

    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
    };
  },
  SIGN_IN: (state, action) => {
    const { user, isAuthenticated, errorCase } = action.payload;

    return {
      ...state,
      isAuthenticated: isAuthenticated,
      user,
      errorCase: errorCase,
    };
  },
  SIGN_UP: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  SIGN_OUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null,
  }),
};

const reducer = (state, action) =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

export const AuthContext = createContext({
  ...initialState,
  issuer: Issuer.JWT,
  signIn: () => Promise.resolve(),
  signUp: () => Promise.resolve(),
  signOut: () => Promise.resolve(),
});

export const AuthProvider = (props) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialState);

  const initialize = useCallback(async () => {
    var user = {};

    try {
      // Try to decode token from HttpOnly cookie
      try {
        const response = await axios.post('/module/decodeToken', {}, {
          withCredentials: true
        });
        user = response.data;
        
        dispatch({
          type: ActionType.INITIALIZE,
          payload: {
            isAuthenticated: true,
            user,
          },
        });
      } catch (error) {
        dispatch({
          type: ActionType.INITIALIZE,
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    } catch (err) {
      console.error(err);
      dispatch({
        type: ActionType.INITIALIZE,
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    }
  }, [dispatch]);

  useEffect(
    () => {
      initialize();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const signIn = useCallback(
    async (data) => {
      var user = {};
      try {
        const response = await axios.post('/module/login', data, {
          withCredentials: true
        });
        
        // Check if session conflict (202 status)
        if (response.status === 202) {
          return response.data;
        }
        
        // For cross-origin, token is in header, for same-origin it's in cookie
        const responseUser = await axios.post('/module/decodeToken', {}, {
          withCredentials: true
        });

        const userPermissions = await axios.get(`/module/user/permission?id=${responseUser.data.userId}`, {
          withCredentials: true
        });

        // Store user details and permissions in sessionStorage for UI purposes only
        const stringifiedUserDetails = JSON.stringify(responseUser.data);
        const stringifiedUserPermission = JSON.stringify(userPermissions.data);

        sessionStorage.setItem(userPermission, stringifiedUserPermission);
        sessionStorage.setItem(userDetails, stringifiedUserDetails);

        user = responseUser.data;

        dispatch({
          type: ActionType.SIGN_IN,
          payload: {
            isAuthenticated: true,
            user,
          },
        });
        
        return { success: true };
      } catch (error) {
        console.log("error case", error.response);
        
        // Handle 202 status in error response
        if (error.response?.status === 202) {
          return error.response.data;
        }
        
        const errorMessage = error.response?.data?.message || error.message;
        dispatch({
          type: ActionType.SIGN_IN,
          payload: {
            isAuthenticated: false,
            user: null,
            errorCase: errorMessage,
          },
        });
        throw new Error(errorMessage);
      }
    },
    [dispatch]
  );

  const signUp = useCallback(
    async (email, name, password) => {
      const { accessToken } = await authApi.signUp({ email, name, password });
      const user = await authApi.me({ accessToken });
      // Token is now handled via HttpOnly cookies
      dispatch({
        type: ActionType.SIGN_UP,
        payload: {
          user,
        },
      });
    },
    [dispatch]
  );

  const signOut = useCallback(async () => {
    await axios.post('/module/logout', {});    
    sessionStorage.removeItem(userDetails);
    sessionStorage.removeItem(userPermission);
    
    dispatch({ type: ActionType.SIGN_OUT });
  }, [dispatch]);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        issuer: Issuer.JWT,
        signIn,
        signUp,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
export const AuthConsumer = AuthContext.Consumer;